import { ClientGroupMembershipStatusEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Menu, Portal, Text } from 'react-native-paper';

import { ArrowForwardIcon, MoreVerticalIcon, WavingHandIcon } from '@/assets';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useGetPendingMembers } from '@/features/api/hooks/grapevine-group/useGetPendingMembers';
import { useJoinGroup } from '@/features/api/hooks/grapevine-group-events/useJoinGroup';
import { useLeaveGroup } from '@/features/api/hooks/grapevine-group-events/useLeaveGroup';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { useFlag } from '@/features/flag/providers/useFlag';
import { FlagEntityTypeEnum } from '@/features/flag/types/flagEntityType';
import { GroupPostFeed } from '@/features/groups/components/feed/GroupPostFeed';
import { useIsGroupAdmin } from '@/features/groups/hooks/useIsGroupAdmin';
import { GroupsRoute } from '@/features/groups/navigation/groupsRoute';
import { Alert } from '@/features/popup/components/Alert';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute } from '@/navigation/types/navigation';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    requests: {
      backgroundColor: theme.colors.tertiary
    },
    button: {
      width: '100%'
    },
    requestLabel: {
      color: theme.colors.primary,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontWeight: 500,
      fontSize: 16,
      width: '100%'
    }
  });

export type Props = AppNativeStackScreenProps<typeof AppRoute.GroupDetail>;

export const GroupDetailScreen: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.group'
  });

  const { isFeatureEnabled } = useFeature();
  const isGroupMemberListEnabled = isFeatureEnabled(FeaturesEnum.GroupMembers);
  const isGroupCreationEnabled = isFeatureEnabled(FeaturesEnum.GroupCreation);

  const { groupId } = route.params;

  const { data: group, isPending } = useGetGroup(groupId);
  const isMember =
    group?.membershipStatus === ClientGroupMembershipStatusEnum.Approved;

  const isCurrentUserAdmin = useIsGroupAdmin(groupId);

  const { flag } = useFlag();

  const iconProps = {
    fill: theme.colors.primary,
    width: 24,
    height: 24
  };

  const [showLeaveAlert, setShowLeaveAlert] = useState<boolean>(false);

  const { data: pendingMembers } = useGetPendingMembers(groupId);
  const pendingRequestsNumber = pendingMembers?.page?.totalElements ?? 0;

  // TODO: pull to refresh pendingMembers

  const joinGroup = useJoinGroup();
  const handleJoinRequest = useCallback(
    () => joinGroup.mutate({ groupId }),
    [groupId, joinGroup]
  );

  const leaveGroup = useLeaveGroup();
  const handleLeaveGroupRequest = useCallback(() => {
    leaveGroup.mutate(groupId, {
      onSuccess: () => {
        setShowLeaveAlert(false);
        navigation.pop();
      }
    });
  }, [groupId, leaveGroup, navigation]);

  const handleTitlePress = useCallback(() => {
    navigation.navigate(AppRoute.GroupDescription, { groupId });
  }, [groupId, navigation]);

  useEffect(() => {
    // Todo: apply this in other places too: do not update header when data is still loading (if the header content was passed as params)
    if (isPending) return;
    navigation.setOptions({
      header: () => (
        <NavigationBar
          picture={group?.logo}
          onPicturePress={handleTitlePress}
          pictureFallback={{ text: { shortName: group?.shortName } }}
          title={group?.name}
          titleVariant="titleMedium"
          onTitlePress={handleTitlePress}
          subTitle={t('members', { count: group?.memberCount ?? 0 })}
          buttons={[
            {
              name: t('menu.name'),
              asMenu: true,
              icon: MoreVerticalIcon,
              menuItems: (onItemPress) => (
                <>
                  {isGroupMemberListEnabled && (
                    <Menu.Item
                      title={t('menu.members')}
                      onPress={() => {
                        onItemPress();
                        navigation.navigate(AppRoute.GroupMembers, { groupId });
                      }}
                    />
                  )}
                  {isCurrentUserAdmin && isGroupCreationEnabled && (
                    <Menu.Item
                      title={t('menu.edit')}
                      onPress={() => {
                        onItemPress();
                        navigation.navigate(AppRoute.GroupEditor, { groupId });
                      }}
                    />
                  )}
                  {!isMember && (
                    <Menu.Item
                      title={t('menu.joinGroup')}
                      onPress={() => {
                        onItemPress();
                        handleJoinRequest();
                      }}
                    />
                  )}
                  <Menu.Item
                    title={t('menu.reportViolation')}
                    onPress={() => {
                      onItemPress();
                      flag(FlagEntityTypeEnum.Group, groupId);
                    }}
                  />
                  {isMember && (
                    <Menu.Item
                      title={t('menu.leaveGroup')}
                      titleStyle={{ color: theme.colors.error }}
                      onPress={() => {
                        onItemPress();
                        setShowLeaveAlert(true);
                      }}
                    />
                  )}
                </>
              )
            }
          ]}
        />
      )
    });
  }, [
    t,
    groupId,
    group?.logo,
    group?.memberCount,
    group?.name,
    group?.shortName,
    isMember,
    navigation,
    isCurrentUserAdmin,
    theme.colors.error,
    isPending,
    flag,
    handleJoinRequest,
    handleLeaveGroupRequest,
    isGroupCreationEnabled,
    isGroupMemberListEnabled,
    handleTitlePress
  ]);

  return (
    <>
      {showLeaveAlert && (
        <Portal>
          <Alert
            isVisible
            labels={t('alerts.leave', {
              returnObjects: true
            })}
            dismissable={!leaveGroup.isPending}
            onClose={() => setShowLeaveAlert(false)}
            confirmColor={theme.colors.error}
            isConfirmLoading={leaveGroup.isPending}
            dismissOnConfirm={false}
            onConfirm={handleLeaveGroupRequest}
          />
        </Portal>
      )}
      {isCurrentUserAdmin && pendingRequestsNumber > 0 && (
        <NavigationListItem
          backgroundColor={theme.colors.tertiaryContainer}
          screen={GroupsRoute.GroupRequests}
          params={{ groupId }}
          icon={() => <WavingHandIcon {...iconProps} />}
          title={
            <Text variant="titleMedium" style={styles.requestLabel}>
              {t('requests', { count: pendingRequestsNumber })}
            </Text>
          }
          hideDivider
          arrowIcon={() => <ArrowForwardIcon {...iconProps} />}
        />
      )}
      <GroupPostFeed groupId={groupId} />
    </>
  );
};
